/**
 * Side-sheet for editing an existing V2 property.
 *
 * Reuses the form patterns established by the wizard's Step 1 + Step 2 +
 * the polished Lessor form (sectioned cards, blur-mode validation, ⌘+Enter
 * to save). Mutates via useUpdateProperty + useUpsertPropertyBankOverride
 * — both already in place.
 *
 * Mounted as a controlled <Sheet> so it can be opened from any header
 * action button. Doesn't deep-link; for that, route at
 * /admin/properties/:id/edit later if needed.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, MapPinOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Section } from "@/components/admin/v2/primitives/Section";
import { Field } from "@/components/admin/v2/primitives/Field";
import { BilingualTextarea } from "@/components/admin/v2/BilingualTextarea";
import {
  bilingualFromRow,
  bilingualToTranslationsJsonb,
} from "@/lib/v2-translations";
import { parseOptionalNumber } from "@/lib/v2-numeric";
import { normalizeVirtualTourUrl } from "@/lib/virtual-tour-url";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUploader } from "@/components/admin/v2/ImageUploader";
import {
  resolvePlatformAccount,
  useDeletePropertyBankOverride,
  usePlatformBankAccounts,
  usePlatformLessor,
  useUpdateProperty,
  useUpsertPropertyBankOverride,
} from "@/hooks/v2/useProperties";
import { useLessor } from "@/hooks/v2/useLessors";
import { usePropertyRepresentorGaps } from "@/hooks/v2/usePropertyRepresentorGaps";
import { extractImageUrls } from "@/lib/v2-images";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import {
  composeStreetAddress,
  isPartialStreetAddress,
  validateStreetName,
} from "@/lib/address";
import {
  resolveCoordinateNotice,
  resolveCoordinatePatch,
} from "@/lib/coordinates";
import { AddressAutocomplete } from "@/components/shared/AddressAutocomplete";
import {
  COLLECTION_MODES,
  PICKUP_METHODS,
  PROPERTY_TYPES,
  REVENUE_MODELS,
  type CollectionMode,
  type PickupMethod,
  type Property,
  type PropertyBankOverride,
  type PropertyType,
  type RevenueModel,
} from "@/types/property";
import { collectionModeLabels } from "@/lib/collection-mode";
import {
  isKeynestDefaultInstructions,
  KEYNEST_DEFAULT_INSTRUCTIONS,
} from "@/lib/key-pickup";
import { PaymentMethodSwitchRow } from "@/components/admin/v2/PaymentMethodRow";
import {
  OWNER_COLLECTS_LOCK_REASON,
  PAYMENT_METHODS,
  PLATFORM_DEFAULT_METHODS,
  methodsLockedByCollectionMode,
} from "@/lib/payment-methods";
import { REVENUE_MODEL_LABELS } from "@/lib/revenue-model";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";

function pickupMethodLabels(
  t: ReturnType<typeof useLanguage>["t"]
): Record<PickupMethod, string> {
  return {
    keynest: t("admin.property.pickupMethod.keynest", "KeyNest partner shop"),
    manual: t(
      "admin.property.pickupMethod.manual",
      "Manual address (non-KeyNest)"
    ),
  };
}

const PLATFORM_INHERIT = "__default__";
// Sentinel for "no property-level revenue model — defined per apartment".
// Radix Select can't use an empty-string item value, so we map "" <-> this.
const REVENUE_NONE = "__none__";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property: Property;
  bankOverride: PropertyBankOverride | null;
  /**
   * Section to scroll into view when the sheet opens — set by PropertyDetail
   * from `?edit=revenue`, the deep link a management-invoice row's "Payment
   * configuration" action follows (COL-287).
   */
  focusSection?: "revenue" | null;
};

/** Apartment labels for the representor messages, capped so a large
 *  property can't produce a wall of text. */
function apartmentLabelList(apartments: { label: string }[]): string {
  const shown = apartments.slice(0, 5).map((a) => a.label);
  const rest = apartments.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
}

/** DOM id of the section `focusSection` scrolls to. */
const SECTION_ID_REVENUE = "property-edit-section-revenue";
/** DOM id of the representor "Required" hint, for the input's aria-describedby. */
const REP_REQUIRED_HINT_ID = "property-edit-rep-required";

type FormState = {
  title: string;
  address: string;
  street_name: string;
  street_number: string;
  city: string;
  zip_code: string;
  /**
   * Coordinates from the address autocomplete's geocoding response. `null`
   * means "leave whatever the row already has" — editing an unrelated field
   * must never wipe a good geocode, and typing a street by hand must never
   * invent one. Only picking a suggestion sets these.
   */
  lat: number | null;
  lng: number | null;
  /**
   * True once the street/house-number/city/zip text has been hand-edited
   * after a pick set `lat`/`lng` (or after opening a property that already
   * had coordinates) without a fresh pick confirming a new point. Distinct
   * from `lat`/`lng` being merely `null`: that alone is ambiguous between
   * "never touched this session, leave the row's geocode alone" and "just
   * invalidated it, the row's stored geocode is now stale for this address."
   * `handleSubmit` uses this to explicitly write the ungeocoded sentinel
   * rather than silently omitting `lat`/`lng` from the patch.
   */
  geocodeInvalidated: boolean;
  type: PropertyType | "";
  revenue_model: RevenueModel | "";
  coliviq_fee_pct: string;
  coliviq_fee_amount: string;
  collection_mode: CollectionMode;
  rent_reminders_enabled: boolean;
  /** Mahngebühr per dunning stage in EUR (COL-308). Kept as string like the
   *  other numeric inputs; empty = fall back to the €5 default on save. */
  dunning_fee_per_stage_eur: string;
  /** How tenants may pay rent here — the default this property's apartments and
   *  rooms inherit. Stored nullable (NULL = platform default) but edited as
   *  plain booleans: a property that someone opened and saved has an opinion. */
  stripe_card_enabled: boolean;
  bank_transfer_enabled: boolean;
  sepa_debit_enabled: boolean;
  platform_bank_account_id: string; // PLATFORM_INHERIT or numeric id as string
  /** Default-language (English) description. Mirrored to the legacy
   *  `description` column on submit; primary source on read is
   *  description_translations.en. */
  descriptionEn: string;
  /** German translation. Empty = not set, omitted from JSONB on save. */
  descriptionDe: string;
  lessor_id: string | null;
  rep_override_name: string;
  rep_override_note: string;
  master_rent: string;
  lease_start: string;
  lease_end: string;
  notice_period: string;
  images: string[];
  /** Optional 360°/Matterport tour URL. Shown next to the photo gallery
   *  in the public listing; null on properties that don't have a tour. */
  virtual_tour_url: string;
  bank_holder: string;
  bank_iban: string;
  bank_bic: string;
  bank_name: string;
  /** Default key pickup location (partner shop). Inherited by apartments + rooms. */
  keynest_pickup_location: string;
  /** Default key pickup address. Inherited by apartments + rooms. */
  keynest_pickup_address: string;
  /** Optional property-level KeyNest link fallback (per-room links override). */
  keynest_access_url: string;
  /** Key pickup method: KeyNest partner shop or a manual / non-KeyNest address. */
  pickup_method: PickupMethod;
  /** Free-text pickup instructions shown to the tenant (mainly manual pickup). */
  pickup_instructions: string;
};

function fromProperty(
  p: Property,
  bank: PropertyBankOverride | null
): FormState {
  const desc = bilingualFromRow({
    description: p.description,
    description_translations: p.description_translations,
  });
  // rep_override may contain `{name, note}` (post #3 representor PR) or
  // `{name, role, note}` (legacy); we drop legacy `role` on save by writing
  // only `{name, note}`.
  const repOverride = p.rep_override ?? null;
  const repName = repOverride?.name ?? "";
  const repNote = repOverride?.note ?? "";
  // A row saved before `address` became derived-only can carry a value that
  // drifted from street_name/street_number (COL-218's original drift). Now
  // that the sheet no longer exposes a field to fix it directly, recompose
  // on open whenever either structured field is set — same as `patch` does
  // on every edit — and only fall back to the stored value when both are
  // still blank.
  const composedAddress =
    p.street_name || p.street_number
      ? composeStreetAddress(p.street_name, p.street_number)
      : (p.address ?? "");
  return {
    title: p.title ?? "",
    address: composedAddress,
    street_name: p.street_name ?? "",
    street_number: p.street_number ?? "",
    city: p.city ?? "",
    zip_code: p.zip_code ?? "",
    lat: null,
    lng: null,
    geocodeInvalidated: false,
    type: (p.type ?? "") as PropertyType | "",
    revenue_model: (p.revenue_model ?? "") as RevenueModel | "",
    coliviq_fee_pct: p.coliviq_fee_pct != null ? String(p.coliviq_fee_pct) : "",
    coliviq_fee_amount:
      p.coliviq_fee_amount != null ? String(p.coliviq_fee_amount) : "",
    collection_mode: (p.collection_mode ??
      "company_collects") as CollectionMode,
    rent_reminders_enabled: p.rent_reminders_enabled ?? true,
    dunning_fee_per_stage_eur:
      p.dunning_fee_per_stage_eur != null
        ? String(p.dunning_fee_per_stage_eur)
        : "5",
    // NULL here means the property has never stated a preference, so show what
    // its rooms actually resolve to today: the platform default.
    stripe_card_enabled:
      p.stripe_card_enabled ?? PLATFORM_DEFAULT_METHODS.stripe_card,
    bank_transfer_enabled:
      p.bank_transfer_enabled ?? PLATFORM_DEFAULT_METHODS.bank_transfer,
    sepa_debit_enabled:
      p.sepa_debit_enabled ?? PLATFORM_DEFAULT_METHODS.sepa_debit,
    platform_bank_account_id:
      p.platform_bank_account_id != null
        ? String(p.platform_bank_account_id)
        : PLATFORM_INHERIT,
    descriptionEn: desc.en,
    descriptionDe: desc.de,
    lessor_id: p.lessor_id ?? null,
    rep_override_name: repName,
    rep_override_note: repNote,
    master_rent: p.master_rent != null ? String(p.master_rent) : "",
    lease_start: p.lease_start ?? "",
    lease_end: p.lease_end ?? "",
    notice_period: p.notice_period ?? "",
    images: extractImageUrls(p.images),
    virtual_tour_url: p.virtual_tour_url ?? "",
    bank_holder: bank?.holder ?? "",
    bank_iban: bank?.iban ?? "",
    bank_bic: bank?.bic ?? "",
    bank_name: bank?.bank_name ?? "",
    keynest_pickup_location: p.keynest_pickup_location ?? "",
    keynest_pickup_address: p.keynest_pickup_address ?? "",
    keynest_access_url: p.keynest_access_url ?? "",
    pickup_method: (p.pickup_method ?? "keynest") as PickupMethod,
    pickup_instructions: p.pickup_instructions ?? "",
  };
}

export function PropertyEditSheet({
  open,
  onOpenChange,
  property,
  bankOverride,
  focusSection = null,
}: Props) {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const PICKUP_METHOD_LABELS = pickupMethodLabels(t);
  // Scroll the deep-linked section into view once the sheet's content has
  // mounted. Radix renders SheetContent only while open, so the element does
  // not exist synchronously on the open render — hence the short delay.
  useEffect(() => {
    if (!open || !focusSection) return;
    const id = window.setTimeout(() => {
      document
        .getElementById(SECTION_ID_REVENUE)
        ?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 80);
    return () => window.clearTimeout(id);
  }, [open, focusSection]);
  const updateMut = useUpdateProperty();
  const bankUpsertMut = useUpsertPropertyBankOverride();
  const bankDeleteMut = useDeletePropertyBankOverride();
  const { data: platformAccounts = [] } = usePlatformBankAccounts();
  const { data: coliviqLessor } = usePlatformLessor();
  // Operators see their company name in revenue copy, and never CoLiviq's
  // own bank accounts (COL-352 Phase 5).
  const { platformName, isOperator } = useOperatorBranding();
  // The logged-in editor is not the owner, so no owner name in the labels.
  const COLLECTION_MODE_LABELS = collectionModeLabels(null, platformName);

  const [form, setForm] = useState<FormState>(() =>
    fromProperty(property, bankOverride)
  );

  // Which "no map" notice this sheet should show, resolved through the same
  // helper chain the submit patch uses — so the warning can never describe a
  // save the code does not perform. The 0/0 guard hides the public map
  // silently, which is right for a visitor and wrong for the editor who can
  // fix it (COL-48 follow-up).
  const coordinateNotice = resolveCoordinateNotice(form, property);

  const { data: selectedLessor = null } = useLessor(form.lessor_id);
  // Placeholder/inheritance text for the Representor field below: what the
  // property would fall back to if no rep_override is set, i.e. the linked
  // owner's own default representor (mirrors ApartmentEditSheet's
  // `linkedOwner?.rep_name`).
  const inheritedRepName = selectedLessor?.rep_name?.trim() ?? "";

  // This sheet's representor is the property-level fallback for every
  // apartment that resolves none of its own (COL-466), so check the whole
  // property against the override as currently typed. Staff-only: an
  // owner-portal account can open this sheet too, and the field was never
  // theirs to fill in (same exemption as ApartmentEditSheet).
  const { isOwnerPortalOnly } = useAuth();
  const repGaps = usePropertyRepresentorGaps(property, {
    repOverrideName: form.rep_override_name,
    enabled: open && !isOwnerPortalOnly,
  });

  // Reset form whenever the sheet opens for a different property/snapshot
  useEffect(() => {
    if (open) {
      setForm(fromProperty(property, bankOverride));
      setInstructionsTouched(false);
    }
  }, [open, property, bankOverride]);

  // A KeyNest building with no instructions starts with the default text in
  // the box (same rule as the room sheet); it is only stored once edited, so
  // tenants keep reading it in their own language.
  const [instructionsTouched, setInstructionsTouched] = useState(false);
  const instructionsValue =
    !instructionsTouched &&
    form.pickup_method === "keynest" &&
    !form.pickup_instructions
      ? KEYNEST_DEFAULT_INSTRUCTIONS[language === "de" ? "de" : "en"]
      : form.pickup_instructions;

  const isSubmitting =
    updateMut.isPending || bankUpsertMut.isPending || bankDeleteMut.isPending;

  function patch(p: Partial<FormState>) {
    setForm((f) => {
      const next = { ...f, ...p };
      // Keep the derived `address` column in sync whenever either structured
      // field changes, instead of relying on a second manually-typed field
      // (see composeStreetAddress). A property whose street fields are left
      // untouched keeps whatever `address` it already had.
      if ("street_name" in p || "street_number" in p) {
        next.address = composeStreetAddress(
          next.street_name,
          next.street_number
        );
      }
      return next;
    });
  }

  // owner_collects settles the methods on its own — the resolver forces card and
  // direct debit off and bank transfer on — so the toggles show that outcome and
  // stop being editable rather than promising a choice that gets overruled.
  const paymentMethodsLocked = methodsLockedByCollectionMode(
    form.collection_mode
  );

  // Where a transfer actually lands. get_room_rent_account ignores this
  // sheet's own bank override entirely under company_collects — that field is
  // the OWNER's payout account, only live once owner_collects applies — so a
  // stray value left over from an earlier collection-mode switch must not be
  // shown as the destination here. The bank fields in this same sheet are the
  // property-level override; empty means the owner's default account is used,
  // which lives on the lessor and is edited there.
  const transferIban = form.bank_iban.trim();
  const transferDestination = paymentMethodsLocked
    ? transferIban
      ? `Transfers go to ${transferIban}${
          form.bank_holder.trim() ? ` · ${form.bank_holder.trim()}` : ""
        }`
      : "Transfers go to the owner's default account."
    : "Transfers go to coliviq's collection account — rent is company-collected here.";

  // Live as the editor types, so the "." is flagged at the field rather than
  // only when they hit Save.
  const streetNameError = validateStreetName(form.street_name);

  async function handleSubmit() {
    if (
      !form.title.trim() ||
      !form.address.trim() ||
      !form.city.trim() ||
      !form.zip_code.trim()
    ) {
      toast({
        title: t(
          "admin.property.toast.missingRequiredFields",
          "Missing required fields"
        ),
        description: t(
          "admin.property.toast.missingRequiredFieldsDesc",
          "Title, postal code, and city are required."
        ),
        variant: "destructive",
      });
      return;
    }

    // `address` is derived from street_name/street_number (composeStreetAddress)
    // rather than typed directly, so half-setting them — clearing just one —
    // would otherwise silently overwrite a complete address with a fragment
    // ("12" with no street, or a street with no house number). A property may
    // leave both blank (e.g. a legacy V1 row with no structured fields at
    // all), just not exactly one. See isPartialStreetAddress / COL-460.
    if (isPartialStreetAddress(form.street_name, form.street_number)) {
      toast({
        title: t(
          "admin.property.toast.missingRequiredFields",
          "Missing required fields"
        ),
        description: t(
          "admin.property.toast.streetFieldsIncomplete",
          "Street name and street number must both be filled in, or both left blank."
        ),
        variant: "destructive",
      });
      return;
    }

    // Street name goes onto official documents verbatim (contract §-fields and
    // the Wohnungsgeberbestätigung's §3), so an abbreviation like "Teststr."
    // must never be stored. Blocked here as well as under the field so a save
    // can't slip through when the input is scrolled out of view.
    if (streetNameError) {
      toast({
        title: "Invalid street name",
        description: streetNameError,
        variant: "destructive",
      });
      return;
    }

    // Refuse a save that leaves any apartment with no representor at all —
    // including one that already had none, since typing one here always
    // fixes it. Never on a still-loading or failed lookup, which isn't a real
    // "none" (see usePropertyRepresentorGaps).
    if (repGaps.failed) {
      repGaps.retry();
      toast({
        title: t(
          "admin.properties.couldNotLoadOwnerData",
          "Could not load owner data — representors can't be checked."
        ),
        description: t(
          "admin.apartments.waitAndTrySavingAgain",
          "Please wait a moment and try saving again."
        ),
        variant: "destructive",
      });
      return;
    }
    if (repGaps.pending) {
      toast({
        title: t(
          "admin.apartments.stillLoadingOwnerData",
          "Still loading owner data"
        ),
        description: t(
          "admin.apartments.waitAndTrySavingAgain",
          "Please wait a moment and try saving again."
        ),
        variant: "destructive",
      });
      return;
    }
    if (repGaps.missing.length > 0) {
      toast({
        title: t(
          "admin.apartments.missingRequiredField",
          "Missing required field"
        ),
        description: t(
          "admin.property.toast.apartmentsNeedRepresentor",
          "These apartments would have no representor: {{apartments}}. Enter one in the Representor section, or set one on those apartments or their owners."
        ).replace("{{apartments}}", apartmentLabelList(repGaps.missing)),
        variant: "destructive",
      });
      return;
    }

    // Validate numeric fields before opening the mutation. We bail with
    // a clear toast rather than letting NaN reach Postgres and surfacing
    // a generic "invalid input syntax for type numeric" error far from
    // the actual cause.
    const masterRent = parseOptionalNumber(form.master_rent);
    if (!masterRent.ok) {
      const rawMasterRent = masterRent.raw;
      toast({
        title: t("admin.property.toast.invalidNumber", "Invalid number"),
        description: `${t("admin.property.toast.invalidMasterRentPrefix", 'Master rent "')}${rawMasterRent}${t("admin.property.toast.invalidNumberSuffix", '" is not a valid number. Use digits only (e.g. 1500).')}`,
        variant: "destructive",
      });
      return;
    }

    // Dunning fee per stage (COL-308): the column is NOT NULL DEFAULT 5, so an
    // empty input falls back to the €5 default rather than writing null.
    const dunningFee = parseOptionalNumber(form.dunning_fee_per_stage_eur);
    if (!dunningFee.ok) {
      const rawDunningFee = dunningFee.raw;
      toast({
        title: t(
          "admin.property.toast.invalidDunningFee",
          "Invalid dunning fee"
        ),
        description: `${t("admin.property.toast.invalidDunningFeePrefix", 'Dunning fee per stage "')}${rawDunningFee}${t("admin.property.toast.invalidDunningFeeSuffix", '" is not a valid number. Use digits only (e.g. 5 or 7.5).')}`,
        variant: "destructive",
      });
      return;
    }
    const dunningFeeValue = dunningFee.value ?? 5;

    let coliviqFeePctValue: number | null = null;
    if (form.revenue_model === "pct_of_rent") {
      const parsed = parseOptionalNumber(form.coliviq_fee_pct);
      if (!parsed.ok || parsed.value == null) {
        toast({
          title: isOperator
            ? `Invalid ${platformName} fee`
            : t(
                "admin.property.toast.invalidColiviqFee",
                "Invalid coliviq fee"
              ),
          description: isOperator
            ? `${platformName} fee (%) is required when revenue model is "Percentage of rent".`
            : t(
                "admin.property.toast.coliviqFeeRequiredPct",
                'coliviq fee (%) is required when revenue model is "Percentage of rent".'
              ),
          variant: "destructive",
        });
        return;
      }
      if (parsed.value < 0 || parsed.value > 100) {
        toast({
          title: isOperator
            ? `Invalid ${platformName} fee`
            : t(
                "admin.property.toast.invalidColiviqFee",
                "Invalid coliviq fee"
              ),
          description: isOperator
            ? `${platformName} fee must be between 0 and 100.`
            : t(
                "admin.property.toast.coliviqFeeRange",
                "coliviq fee must be between 0 and 100."
              ),
          variant: "destructive",
        });
        return;
      }
      coliviqFeePctValue = parsed.value;
    }

    let coliviqFeeAmountValue: number | null = null;
    if (form.revenue_model === "flat_fee_per_room") {
      const parsed = parseOptionalNumber(form.coliviq_fee_amount);
      if (!parsed.ok || parsed.value == null) {
        toast({
          title: t("admin.property.toast.invalidFlatFee", "Invalid flat fee"),
          description: t(
            "admin.property.toast.flatFeeRequired",
            'Flat fee (€) is required when revenue model is "Flat fee per room".'
          ),
          variant: "destructive",
        });
        return;
      }
      if (parsed.value < 0) {
        toast({
          title: t("admin.property.toast.invalidFlatFee", "Invalid flat fee"),
          description: t(
            "admin.property.toast.flatFeeMin",
            "Flat fee must be 0 or more."
          ),
          variant: "destructive",
        });
        return;
      }
      coliviqFeeAmountValue = parsed.value;
    }

    try {
      // 1. Update the property core. Bilingual description is mirrored to the
      //    legacy single-language `description` column (EN value) so anything
      //    still reading that column doesn't break, while the canonical source
      //    becomes description_translations.
      const translations = bilingualToTranslationsJsonb({
        en: form.descriptionEn,
        de: form.descriptionDe,
      });
      // rep_override: write { name, note } whenever a name is set; a blank
      // name clears any prior override and falls back to the lessor's
      // defaults. A note without a name is dropped rather than stored,
      // matching ApartmentEditSheet's rep_override save logic.
      const repOverrideName = form.rep_override_name.trim();
      const repOverrideNote = form.rep_override_note.trim();
      const repOverridePayload = repOverrideName
        ? { name: repOverrideName, note: repOverrideNote || null }
        : null;
      await updateMut.mutateAsync({
        id: property.id,
        patch: {
          title: form.title.trim(),
          address: form.address.trim(),
          street_name: form.street_name.trim() || null,
          street_number: form.street_number.trim() || null,
          city: form.city.trim(),
          zip_code: form.zip_code.trim() || null,
          // Omitted when saving an unrelated field, so existing coordinates
          // stay untouched. But once the address was hand-edited after a
          // pick (geocodeInvalidated), form.lat/lng are null and omitting
          // them here would silently leave the row's now-stale coordinates
          // attached to the new address — write the ungeocoded sentinel
          // (0/0, see hasPlottableCoordinates) instead so a stale pin never
          // survives an unconfirmed address change. The same resolver backs
          // the "no map" notice below, so the warning cannot drift from what
          // this actually writes.
          ...(resolveCoordinatePatch(form) ?? {}),
          type: form.type === "" ? null : form.type,
          description: form.descriptionEn.trim() || null,
          description_translations: translations,
          lessor_id: form.lessor_id,
          rep_override: repOverridePayload,
          master_rent: masterRent.value,
          lease_start: form.lease_start || null,
          lease_end: form.lease_end || null,
          notice_period: form.notice_period.trim() || null,
          revenue_model: form.revenue_model === "" ? null : form.revenue_model,
          coliviq_fee_pct: coliviqFeePctValue,
          coliviq_fee_amount: coliviqFeeAmountValue,
          collection_mode: form.collection_mode,
          rent_reminders_enabled: form.rent_reminders_enabled,
          dunning_fee_per_stage_eur: dunningFeeValue,
          // Written as-is even under owner_collects: the resolver clamps card
          // and direct debit off for that mode, so persisting the admin's
          // choice means it comes back intact if the mode is switched later.
          stripe_card_enabled: form.stripe_card_enabled,
          bank_transfer_enabled: form.bank_transfer_enabled,
          sepa_debit_enabled: form.sepa_debit_enabled,
          platform_bank_account_id:
            form.platform_bank_account_id === PLATFORM_INHERIT
              ? null
              : Number(form.platform_bank_account_id),
          image_url: form.images[0] ?? null,
          images: form.images,
          virtual_tour_url:
            normalizeVirtualTourUrl(form.virtual_tour_url) || null,
          keynest_pickup_location: form.keynest_pickup_location.trim() || null,
          keynest_pickup_address: form.keynest_pickup_address.trim() || null,
          keynest_access_url:
            form.pickup_method === "keynest"
              ? form.keynest_access_url.trim() || null
              : null,
          pickup_method: form.pickup_method,
          pickup_instructions:
            isKeynestDefaultInstructions(instructionsValue) ||
            !instructionsValue.trim()
              ? null
              : instructionsValue.trim(),
        },
      });

      // 2. Upsert/delete bank override based on whether IBAN is set
      const ibanTrim = form.bank_iban.trim();
      if (ibanTrim) {
        await bankUpsertMut.mutateAsync({
          property_id: property.id,
          holder: form.bank_holder.trim() || null,
          iban: ibanTrim.toUpperCase(),
          bic: form.bank_bic.trim().toUpperCase() || null,
          bank_name: form.bank_name.trim() || null,
        });
      } else if (bankOverride) {
        // Was previously set; user cleared it → remove the row.
        await bankDeleteMut.mutateAsync(property.id);
      }

      toast({
        title: t("admin.property.toast.propertyUpdated", "Property updated"),
        description: form.title,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t(
          "admin.property.toast.couldNotSaveChanges",
          "Could not save changes"
        ),
        description: formatSupabaseError(err),
        variant: "destructive",
      });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        onKeyDown={handleKeyDown}
      >
        <SheetHeader className="space-y-1">
          <SheetTitle>
            {t("admin.property.editTitle", "Edit property")}
          </SheetTitle>
          <SheetDescription>
            {t(
              "admin.property.editDescPrefix",
              "Changes save directly to the property. Images upload to Supabase Storage as you drop them. Press"
            )}{" "}
            <kbd className="font-mono text-[10px]">⌘ ↵</kbd>{" "}
            {t("admin.property.editDescSuffix", "to save.")}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Property */}
          <Section title={t("admin.property.section.property", "Property")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={t("admin.property.field.title", "Title")}
                required
                className="sm:col-span-2"
              >
                <Input
                  value={form.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  placeholder="e.g. Sunny WG in Schwabing"
                />
              </Field>
              <Field
                label={t("admin.property.field.streetName", "Street name")}
                error={streetNameError}
              >
                <AddressAutocomplete
                  value={form.street_name}
                  onChange={(v) =>
                    // A manual keystroke after picking a suggestion means the
                    // street name no longer matches the geocode that came
                    // with it — drop the stale point and flag it as
                    // invalidated so the submit patch writes the ungeocoded
                    // sentinel instead of silently leaving the row's old
                    // coordinates attached to this new address (COL-48
                    // follow-up).
                    patch({
                      street_name: v,
                      lat: null,
                      lng: null,
                      geocodeInvalidated: true,
                    })
                  }
                  onSelect={({
                    street,
                    houseNumber,
                    postalCode,
                    city,
                    lat,
                    lng,
                  }) => {
                    patch({
                      street_name: street,
                      street_number: houseNumber || form.street_number,
                      city: city || form.city,
                      zip_code: postalCode || form.zip_code,
                      // Re-geocodes the property whenever the address is
                      // re-picked — the only way an existing 0/0 row gets
                      // fixed through the UI.
                      lat: lat ?? form.lat,
                      lng: lng ?? form.lng,
                      geocodeInvalidated: false,
                    });
                  }}
                  placeholder="e.g. Nimmerfallstrasse"
                  aria-invalid={!!streetNameError}
                />
              </Field>
              <Field
                label={t("admin.property.field.streetNumber", "Street number")}
              >
                <Input
                  value={form.street_number}
                  onChange={(e) =>
                    patch({
                      street_number: e.target.value,
                      lat: null,
                      lng: null,
                      geocodeInvalidated: true,
                    })
                  }
                  placeholder="e.g. 37"
                />
              </Field>
              <Field label={t("admin.property.field.city", "City")} required>
                <Input
                  value={form.city}
                  onChange={(e) =>
                    patch({
                      city: e.target.value,
                      lat: null,
                      lng: null,
                      geocodeInvalidated: true,
                    })
                  }
                />
              </Field>
              <Field
                label={t("admin.property.field.zipCode", "Postal code (PLZ)")}
                required
              >
                <Input
                  value={form.zip_code}
                  onChange={(e) =>
                    patch({
                      zip_code: e.target.value,
                      lat: null,
                      lng: null,
                      geocodeInvalidated: true,
                    })
                  }
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="80331"
                />
              </Field>
              {coordinateNotice !== "none" && (
                <p
                  role="status"
                  className="sm:col-span-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                >
                  <MapPinOff
                    className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    aria-hidden
                  />
                  <span>
                    {coordinateNotice === "willClear"
                      ? t(
                          "admin.property.geocode.willClear",
                          "Saving now removes the map from this property's room pages — the edited address has no coordinates yet. Pick it from the suggestions to keep the map."
                        )
                      : t(
                          "admin.property.geocode.missing",
                          "This property has no coordinates, so its room pages show no map. Pick the address from the suggestions to add one."
                        )}
                  </span>
                </p>
              )}
              <Field label={t("admin.property.field.type", "Type")}>
                <Select
                  value={form.type === "" ? undefined : form.type}
                  onValueChange={(v) => patch({ type: v as PropertyType })}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "admin.property.field.selectTypePlaceholder",
                        "Select type…"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((pt) => (
                      <SelectItem key={pt} value={pt}>
                        {pt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t("admin.property.field.description", "Description")}
                className="sm:col-span-2"
              >
                <BilingualTextarea
                  valueEn={form.descriptionEn}
                  valueDe={form.descriptionDe}
                  onChange={({ en, de }) =>
                    patch({ descriptionEn: en, descriptionDe: de })
                  }
                  rows={3}
                  placeholder={t(
                    "admin.property.field.descriptionPlaceholder",
                    "Welcome message, neighborhood description, key amenities…"
                  )}
                />
              </Field>
            </div>
          </Section>

          {/* Representor — the property-level fallback used only when an
              apartment has no room/apartment-level override AND no lessor of
              its own with a rep_name set (resolveEffectiveRep's precedence:
              room → apartment override → apartment's own lessor → this
              property override → property's lessor). An apartment with its
              own lessor whose rep_name is set is NOT overridden by this
              field. Blank = inherit the linked owner's default representor,
              so the placeholder shows what would apply today (mirrors the
              Representor field in ApartmentEditSheet.tsx). */}
          <Section
            title={t("admin.apartments.representor", "Representor")}
            description={t(
              "admin.apartment.representorDescription",
              "The representor is the person or entity who represents the owner in the operations of this property/apartment."
            )}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={t("admin.apartments.representor", "Representor")}
                required={!isOwnerPortalOnly && repGaps.missing.length > 0}
                className="sm:col-span-2"
              >
                <Input
                  value={form.rep_override_name}
                  aria-invalid={repGaps.missing.length > 0 || undefined}
                  aria-describedby={
                    repGaps.missing.length > 0
                      ? REP_REQUIRED_HINT_ID
                      : undefined
                  }
                  onChange={(e) => patch({ rep_override_name: e.target.value })}
                  placeholder={
                    inheritedRepName ||
                    t(
                      "admin.apartments.representorPlaceholder",
                      'e.g. "den Geschäftsführer" or "Coliviq GmbH"'
                    )
                  }
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {form.rep_override_name.trim() ? (
                    <>
                      {t(
                        "admin.property.field.representorOverridePre",
                        "Overrides the owner's default for this property. Clear it to go back to"
                      )}{" "}
                      <span className="font-medium text-foreground">
                        {inheritedRepName ||
                          t("admin.apartments.noRepresentor", "no representor")}
                      </span>
                      .
                    </>
                  ) : inheritedRepName ? (
                    <>
                      {t(
                        "admin.apartments.inheritedFromOwner",
                        "Inherited from the owner:"
                      )}{" "}
                      <span className="font-medium text-foreground">
                        {inheritedRepName}
                      </span>
                      .{" "}
                      {t(
                        "admin.property.field.typeHereToOverride",
                        "Type here to override it for this property."
                      )}
                    </>
                  ) : (
                    <>
                      {t(
                        "admin.property.field.representorNone",
                        "No representor set on the owner. Enter one here to set it for this property."
                      )}
                    </>
                  )}
                </p>
                {repGaps.missing.length > 0 ? (
                  // Blocks Save (handleSubmit) — say so here rather than
                  // leaving a silent blocker.
                  <p
                    id={REP_REQUIRED_HINT_ID}
                    role="status"
                    className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive"
                  >
                    <AlertTriangle
                      className="mt-px h-3.5 w-3.5 shrink-0"
                      aria-hidden
                    />
                    <span>
                      {t(
                        "admin.property.field.apartmentsNeedRepresentor",
                        "Required. No representor would apply to: {{apartments}}. Enter one here to cover them, or set one on those apartments or their owners."
                      ).replace(
                        "{{apartments}}",
                        apartmentLabelList(repGaps.missing)
                      )}
                    </span>
                  </p>
                ) : repGaps.failed ? (
                  <p className="mt-1.5 text-xs text-destructive">
                    {t(
                      "admin.properties.couldNotLoadOwnerData",
                      "Could not load owner data — representors can't be checked."
                    )}{" "}
                    <button
                      type="button"
                      className="underline"
                      onClick={repGaps.retry}
                    >
                      {t("common.action.tryAgain", "Try again")}
                    </button>
                  </p>
                ) : (
                  repGaps.pending && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {t(
                        "admin.property.field.checkingApartmentRepresentors",
                        "Checking whether every apartment has a representor…"
                      )}
                    </p>
                  )
                )}
              </Field>
              <Field
                label={t(
                  "admin.apartments.representorNote",
                  "Representor note"
                )}
                className="sm:col-span-2"
              >
                <Textarea
                  rows={2}
                  value={form.rep_override_note}
                  onChange={(e) => patch({ rep_override_note: e.target.value })}
                  placeholder={
                    selectedLessor?.rep_note?.trim() ||
                    t(
                      "admin.property.field.representorNotePlaceholder",
                      "Optional. Internal note about who signs for this property."
                    )
                  }
                />
                {form.rep_override_note.trim() &&
                  !form.rep_override_name.trim() && (
                    <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-500">
                      {t(
                        "admin.apartments.addRepresentorForNote",
                        "Add a representor above for this note to be saved — a note on its own is not kept."
                      )}
                    </p>
                  )}
              </Field>
            </div>
          </Section>

          {/* Revenue model — COL-222/223. Modeled around CoLiviq's fee
              percentage, not owner share. full_rent ignores the fee. */}
          <Section
            id={SECTION_ID_REVENUE}
            title={t("admin.property.section.revenueModel", "Revenue model")}
            description={
              isOperator
                ? `How ${platformName} earns from this property. Switch models freely — fields hidden by the inactive model aren't persisted.`
                : t(
                    "admin.property.section.revenueModelDesc",
                    "How coliviq earns from this property. Switch models freely — fields hidden by the inactive model aren't persisted."
                  )
            }
            defaultOpen={focusSection === "revenue"}
          >
            <div className="space-y-3">
              <Field
                label={t(
                  "admin.property.field.collectionMode",
                  "Collection mode"
                )}
                hint={t(
                  "admin.property.field.collectionModeHint",
                  "Default for this property's apartments unless an apartment overrides it."
                )}
              >
                <Select
                  value={form.collection_mode}
                  onValueChange={(v) =>
                    patch({ collection_mode: v as CollectionMode })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLECTION_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {COLLECTION_MODE_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t(
                  "admin.property.field.rentReminders",
                  "Rent reminders"
                )}
                hint={t(
                  "admin.property.field.rentRemindersHint",
                  "Turn off when the owner collects rent directly and we have no bank visibility — suppresses overdue detection, reminder emails and dunning for this property's units. Apartments can override."
                )}
              >
                <div className="flex items-center gap-2">
                  <Switch
                    id="rent-reminders-toggle"
                    checked={form.rent_reminders_enabled}
                    onCheckedChange={(v) =>
                      patch({ rent_reminders_enabled: v })
                    }
                  />
                  <Label
                    htmlFor="rent-reminders-toggle"
                    className="text-sm cursor-pointer"
                  >
                    {form.rent_reminders_enabled
                      ? t("admin.property.field.remindersOn", "Reminders on")
                      : t(
                          "admin.property.field.remindersOff",
                          "Reminders off (not tracked)"
                        )}
                  </Label>
                </div>
              </Field>
              <Field
                label={t(
                  "admin.property.field.dunningFee",
                  "Mahngebühr pro Stufe / Dunning fee per stage"
                )}
                hint={t(
                  "admin.property.field.dunningFeeHint",
                  "Added per Mahnung sent from stage 2 onward (1. Mahnung, 2. Mahnung, Inkasso re-sends). Default €5."
                )}
              >
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step="0.5"
                    inputMode="decimal"
                    className="max-w-[140px]"
                    value={form.dunning_fee_per_stage_eur}
                    onChange={(e) =>
                      patch({ dunning_fee_per_stage_eur: e.target.value })
                    }
                    placeholder="5"
                  />
                  <span className="text-sm text-muted-foreground">€</span>
                </div>
              </Field>
              <Field
                label={t("admin.property.field.model", "Model")}
                hint={t(
                  "admin.property.field.modelHint",
                  "Optional — leave as “Not set” if revenue is defined per apartment."
                )}
              >
                <Select
                  value={form.revenue_model || REVENUE_NONE}
                  onValueChange={(v) =>
                    patch({
                      revenue_model:
                        v === REVENUE_NONE ? "" : (v as RevenueModel),
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={REVENUE_NONE}>
                      {t(
                        "admin.property.field.notSetPerApartment",
                        "Not set — defined per apartment"
                      )}
                    </SelectItem>
                    {REVENUE_MODELS.map((rm) => (
                      <SelectItem key={rm} value={rm}>
                        {REVENUE_MODEL_LABELS[rm]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {form.revenue_model === "pct_of_rent" && (
                <Field
                  label={
                    isOperator
                      ? `${platformName} fee (%)`
                      : t(
                          "admin.property.field.coliviqFeePct",
                          "coliviq fee (%)"
                        )
                  }
                  hint={
                    isOperator
                      ? `${platformName}'s cut of tenant rent total. Owner share is the remainder (100 − fee).`
                      : t(
                          "admin.property.field.coliviqFeePctHint",
                          "coliviq's cut of tenant rent total. Owner share is the remainder (100 − fee)."
                        )
                  }
                  required
                >
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    inputMode="decimal"
                    value={form.coliviq_fee_pct}
                    onChange={(e) => patch({ coliviq_fee_pct: e.target.value })}
                    placeholder="25"
                  />
                </Field>
              )}
              {form.revenue_model === "flat_fee_per_room" && (
                <Field
                  label={t(
                    "admin.property.field.flatFeePerRoom",
                    "Flat fee (€ / room)"
                  )}
                  hint={
                    isOperator
                      ? `${platformName} earns a fixed fee per occupied room.`
                      : t(
                          "admin.property.field.flatFeePerRoomHint",
                          "coliviq earns a fixed fee per occupied room."
                        )
                  }
                  required
                >
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={form.coliviq_fee_amount}
                    onChange={(e) =>
                      patch({ coliviq_fee_amount: e.target.value })
                    }
                    placeholder="120"
                  />
                </Field>
              )}
              {form.revenue_model === "full_rent" && (
                <p className="text-xs text-muted-foreground">
                  {isOperator
                    ? `${platformName} pays the owner a fixed master rent and keeps whatever it collects from tenants. Margin = tenant rent total − master rent.`
                    : t(
                        "admin.property.field.fullRentExplainer",
                        "coliviq pays the owner a fixed master rent and keeps whatever it collects from tenants. Margin = tenant rent total − master rent."
                      )}
                </p>
              )}
              {/* Platform bank account picker reads CoLiviq's own accounts
                  (usePlatformBankAccounts/usePlatformLessor) — never shown to
                  operators; the hooks also skip their queries for them. */}
              {!isOperator && (
                <Field
                  label={t(
                    "admin.property.field.platformBankAccount",
                    "coliviq bank account"
                  )}
                  hint={t(
                    "admin.property.field.platformBankAccountHint",
                    "Which coliviq-held account this property's flows use. The default is the coliviq lessor profile's bank account."
                  )}
                >
                  <Select
                    value={form.platform_bank_account_id}
                    onValueChange={(v) =>
                      patch({ platform_bank_account_id: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PLATFORM_INHERIT}>
                        {t(
                          "admin.property.field.useDefaultColiviqAccount",
                          "Use default coliviq account"
                        )}
                        {(() => {
                          const def = resolvePlatformAccount({
                            accounts: platformAccounts,
                            coliviqLessor: coliviqLessor ?? null,
                          });
                          return def ? ` (${def.label})` : "";
                        })()}
                      </SelectItem>
                      {platformAccounts.map((acc) => (
                        <SelectItem key={acc.id} value={String(acc.id)}>
                          {acc.label}
                          {acc.is_default
                            ? ` (${t("admin.property.field.defaultSuffix", "default")})`
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          </Section>

          {/* Payment methods — the property-level default for how a
              tenant may pay rent here. Apartments override it, rooms override
              them, and get_room_payment_methods resolves the chain for the
              tenant UI and the two SEPA charge functions.
              Named "Payment methods", not "Payment configuration" — that
              label is already taken by the COL-287 management-invoice link
              (PaymentConfigurationAnchor), which points at Revenue model, a
              different section entirely. */}
          <Section
            title="Payment methods"
            description="How tenants can pay rent in this property. Apartments can override any of these."
          >
            <div className="space-y-3">
              {paymentMethodsLocked && (
                <p className="rounded-md border border-l-4 border-l-destructive bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  {OWNER_COLLECTS_LOCK_REASON}
                </p>
              )}
              {PAYMENT_METHODS.map((method) => {
                // Under owner_collects the row shows what the resolver will
                // actually do, not what the column happens to hold.
                const checked = paymentMethodsLocked
                  ? method.key === "bank_transfer"
                  : form[method.column];
                return (
                  <PaymentMethodSwitchRow
                    key={method.key}
                    id={`payment-method-${method.key}`}
                    label={method.label}
                    description={method.description}
                    checked={checked}
                    disabled={paymentMethodsLocked}
                    note={
                      paymentMethodsLocked ? (
                        <Badge variant="outline" className="font-normal">
                          {method.key === "bank_transfer"
                            ? "Always on while the owner collects"
                            : "Locked off while the owner collects"}
                        </Badge>
                      ) : property[method.column] == null ? (
                        // The column is still NULL: what the switch shows is
                        // the CoLiviq default, not a decision anyone made here.
                        // Saving the sheet is what turns it into one.
                        <Badge variant="outline" className="font-normal">
                          Not set — showing the coliviq default
                        </Badge>
                      ) : null
                    }
                    onCheckedChange={(v) =>
                      patch({ [method.column]: v } as Partial<FormState>)
                    }
                  />
                );
              })}
              <p className="border-t border-dashed pt-3 text-xs text-muted-foreground">
                {transferDestination}
              </p>
            </div>
          </Section>

          {/* Key pickup (KeyNest) — property-level source of truth. The
              pickup location is shared across the building and inherited by
              every apartment + room unless they override it. The KeyNest key
              link itself stays per-room (set on each room), never here. */}
          <Section
            title={t("admin.property.section.keyPickup", "Key pickup")}
            description={t(
              "admin.property.section.keyPickupDesc",
              "Where tenants collect their keys. This is the default for every apartment and room in this property; an apartment or room can override it."
            )}
          >
            <div className="grid grid-cols-1 gap-3">
              <Field
                label={t("admin.property.field.pickupMethod", "Pickup method")}
                hint={t(
                  "admin.property.field.pickupMethodHint",
                  "KeyNest shows the access link + code guidance to the tenant. Manual shows the address and instructions only."
                )}
              >
                <Select
                  value={form.pickup_method}
                  onValueChange={(v) =>
                    patch({ pickup_method: v as PickupMethod })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PICKUP_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {PICKUP_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t(
                  "admin.property.field.pickupLocation",
                  "Pickup location"
                )}
                hint={
                  form.pickup_method === "keynest"
                    ? t(
                        "admin.property.field.pickupLocationHintKeynest",
                        "The KeyNest / partner shop name, e.g. “Kiosk am Hauptbahnhof”."
                      )
                    : t(
                        "admin.property.field.pickupLocationHintManual",
                        "Name of the pickup point, e.g. “Building reception” or “Property office”."
                      )
                }
              >
                <Input
                  value={form.keynest_pickup_location}
                  onChange={(e) =>
                    patch({ keynest_pickup_location: e.target.value })
                  }
                  placeholder={
                    form.pickup_method === "keynest"
                      ? "e.g. Kiosk am Hauptbahnhof"
                      : "e.g. Building reception"
                  }
                />
              </Field>
              <Field
                label={t(
                  "admin.property.field.pickupAddress",
                  "Pickup address"
                )}
                hint={t(
                  "admin.property.field.pickupAddressHint",
                  "Street, house number, postal code, city of the pickup point."
                )}
              >
                <Textarea
                  rows={2}
                  value={form.keynest_pickup_address}
                  onChange={(e) =>
                    patch({ keynest_pickup_address: e.target.value })
                  }
                  placeholder="Bahnhofstr. 5, 80335 München"
                />
              </Field>
              {form.pickup_method === "keynest" ? (
                <Field
                  label={t(
                    "admin.property.field.keynestAccessLink",
                    "KeyNest access link (fallback)"
                  )}
                  hint={t(
                    "admin.property.field.keynestAccessLinkHint",
                    "Only for single-key properties where every tenant uses the same link. Each room can set its own link, which always overrides this. Leave blank if keys differ per room."
                  )}
                >
                  <Input
                    type="url"
                    inputMode="url"
                    value={form.keynest_access_url}
                    onChange={(e) =>
                      patch({ keynest_access_url: e.target.value })
                    }
                    placeholder="https://keynest.com/…"
                  />
                </Field>
              ) : null}
              <Field
                label={t(
                  "admin.property.field.pickupInstructions",
                  "Pickup instructions"
                )}
                hint={t(
                  "admin.property.field.pickupInstructionsHint",
                  "Optional. Shown to the tenant, e.g. “Ring the bell at reception and ask for your key during opening hours.”"
                )}
              >
                <Textarea
                  rows={2}
                  value={instructionsValue}
                  onChange={(e) => {
                    setInstructionsTouched(true);
                    patch({ pickup_instructions: e.target.value });
                  }}
                  placeholder={t(
                    "admin.property.field.pickupInstructionsPlaceholder",
                    "Ring the bell at reception during opening hours."
                  )}
                />
              </Field>
            </div>
          </Section>

          {/* Photos */}
          <Section title={t("admin.property.section.photos", "Photos")}>
            <ImageUploader
              label=""
              hint={t(
                "admin.property.field.propertyPhotosHint",
                "Drag thumbnails to reorder. The first image is the cover; the star button makes any image primary in one click."
              )}
              value={form.images}
              onChange={(images) => patch({ images })}
              maxImages={20}
            />
            <Field
              label={t(
                "admin.property.field.virtualTourUrl",
                "Virtual tour URL"
              )}
              hint={t(
                "admin.property.field.virtualTourUrlHint",
                "Optional. Matterport, Kuula, YouTube 360°, etc. Shown next to the photo gallery on the public listing."
              )}
              className="mt-3"
            >
              <Input
                type="url"
                inputMode="url"
                value={form.virtual_tour_url}
                onChange={(e) => patch({ virtual_tour_url: e.target.value })}
                onBlur={(e) =>
                  patch({
                    virtual_tour_url: normalizeVirtualTourUrl(e.target.value),
                  })
                }
                placeholder="https://my.matterport.com/show/?m=…"
              />
            </Field>
          </Section>
        </div>

        <SheetFooter className="mt-6 gap-2 sticky bottom-0 bg-background pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t("admin.action.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("admin.action.saving", "Saving…")}
              </>
            ) : (
              t("admin.action.saveChanges", "Save changes")
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────
